import React, { useState, useLayoutEffect } from 'react';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import classes from './invoices.module.scss';
import { useQuery } from '../../../../utils/hooks/useQuery';
import Button from '../../../_common/Form/Buttons/Button';
import Select from '../../../_common/Form/Selects/Select/Select';
import Loader from '../../../_common/Loader/Loader';
import { getSelectRepListRequest } from '../../../../store/actions/users';
import RangeMonthPicker from '../../../_common/Form/DatePickers/MonthPicker/RangeMonthPicker';
import Api from '../../../../api/api';
import InvoiceTable from './InvoiceTable';
import RepBonusModal from '../RepBonus/RepBonusModal';
import AttentionModal from '../../../_common/Modal/AttentionModal/AttentionModal';

const tableHeader = [
  {
    name: 'Invoice Number',
    path: 'invoiceNumber',
  },
  {
    name: 'Rep',
    path: 'rep',
  },
  {
    name: 'Date',
    path: 'date',
  },
  {
    name: 'Bill Amount',
    path: 'totalAmount',
    type: 'currency',
  },
  {
    name: 'Status',
    path: 'status',
  },
  {
    name: 'Amount Due',
    path: 'paidTotal',
    type: 'currency',
  },
  {
    name: '',
    type: 'action',
  },
];

const menuOptions = (invoice) => [
  {
    label: 'Pay',
    value: 'pay',
    show: invoice.status !== 'Paid',
  },
  {
    label: 'History',
    value: 'history',
    show: !!invoice.payments.length,
  },
  {
    label: 'Cancel Invoice',
    value: 'cancel',
    show: !!invoice.payments.length,
  },
].filter((i) => i.show);

const thisMonth = moment().format('yyyy-MM');
const limit = 20;

function Invoices() {
  const { query, setQuery } = useQuery();

  const dispatch = useDispatch();
  const { selectRepList, pageCount, currentPage } = useSelector((store) => store.users);

  const [invoices, setInvoices] = useState([]);
  const [requestData, setRequestData] = useState({
    start: query.start ? moment(query.start).format('yyyy-MM') : thisMonth,
    end: query.end && moment(query.start).format('yyyy-MM') !== moment(query.end).format('yyyy-MM')
      ? moment(query.end).format('yyyy-MM') : '',
    name: query.name || '',
    status: query.status || '',
  });

  const [loading, setLoading] = useState(false);

  const [payModalInfo, setPayModalInfo] = useState({});
  const [payModalAmountError, setPayModalAmountError] = useState(false);
  const [savePaymentLoading, savePaymentLoadingToggle] = useState(false);

  const [deleteModalInfo, setDeleteModalInfo] = useState({});

  const getInvoices = async (filters) => {
    const { data } = await Api.getDashboardRepInvoices(filters);

    setInvoices(data.data);
  };

  useLayoutEffect(() => {
    (async () => {
      setLoading(true);

      await Promise.all([
        getInvoices(requestData),
        dispatch(getSelectRepListRequest({ limit, role: 'Rep' })),
      ]);

      setLoading(false);
    })();
  }, []);

  const onChangeDate = (currentDate) => {
    setRequestData((prev) => ({ ...prev, start: currentDate[0], end: currentDate[1] }));
  };

  const changeRep = (name) => {
    setRequestData((prev) => ({ ...prev, name }));
  };

  const changeRequestData = (path, value) => {
    setRequestData((prev) => ({ ...prev, [path]: value }));
  };

  const clearFilters = async () => {
    const defaultRequestData = {
      start: thisMonth,
      end: '',
      rep: '',
      status: '',
    };

    setRequestData(defaultRequestData);

    setQuery(defaultRequestData);

    setLoading(true);
    await getInvoices(defaultRequestData);
    setLoading(false);
  };

  const nextOptionListRequest = async (p) => {
    await dispatch(getSelectRepListRequest({ page: p, limit, role: 'Rep' }));
  };

  const onUpdateClick = async () => {
    setLoading(true);
    await getInvoices(requestData);

    setQuery(requestData);
    setLoading(false);
  };

  const onInvoiceClick = (data) => {
    setPayModalInfo({
      isOpen: true,
      title: `Invoce N ${data.invoiceNumber}`,
      payments: data.payments,
      date: moment(data.date).format('MMMM YYYY'),
    });
  };

  const onMenuSelect = (type, data) => {
    if (type === 'pay') {
      setPayModalInfo({
        isOpen: true,
        userId: data.repId,
        paymentMethod: 'Cash',
        checkNumber: '',
        date: moment(data.date).format('YYYY-MM'),
        amount: data.remainder.toString(),
      });
    } else if (type === 'history') {
      setPayModalInfo({
        isOpen: true,
        title: `Invoce N ${data.invoiceNumber}`,
        payments: data.payments,
        date: moment(data.date).format('MMMM YYYY'),
      });
    } else {
      setDeleteModalInfo({
        userId: data.repId,
        date: moment(data.date).format('YYYY-MM'),
      });
    }
  };

  const closeModal = () => {
    if (!savePaymentLoading) setPayModalInfo({});
  };

  const changeModalData = (path, value) => {
    setPayModalInfo((prev) => {
      const newData = { ...prev, [path]: value };

      if (path === 'paymentMethod') {
        newData.checkNumber = '';
      }

      return newData;
    });

    if (path === 'amount') setPayModalAmountError(false);
  };

  const pay = async (e) => {
    e.preventDefault();

    if (!payModalInfo.amount?.trim()) {
      setPayModalAmountError(true);

      return toast.error('Please fill Amount field');
    }

    try {
      savePaymentLoadingToggle(true);

      await Api.payBonus(payModalInfo);

      await getInvoices(requestData);
      setPayModalInfo({});
    } catch (err) {
      toast.error('Something went wrong');
      console.warn(err);
    }

    savePaymentLoadingToggle(false);
  };

  const cancelPayment = async () => {
    try {
      savePaymentLoadingToggle(true);

      await Api.deleteInvoice(deleteModalInfo);

      await getInvoices(requestData);

      if (payModalInfo.payments) {
        const filteredPayments = payModalInfo.payments.filter((p) => p.id !== deleteModalInfo.id);

        if (!filteredPayments.length) {
          setPayModalInfo({});
        } else {
          setPayModalInfo((prev) => ({
            ...prev, payments: filteredPayments,
          }));
        }
      }
      setDeleteModalInfo({});
    } catch (err) {
      toast.error('Something went wrong');
      console.warn(err);
    }

    savePaymentLoadingToggle(false);
  };

  return (
    <div className={classes.wrapper}>
      <div className={classes.wrapper_header}>
        <div style={{ marginRight: 10 }}>
          <RangeMonthPicker
            startDate={requestData.start}
            endDate={requestData.end}
            onChange={onChangeDate}
            defaultThisMonth
          />
        </div>

        <div className={classes.status}>
          <Select
            label="Status"
            options={['Unpaid', 'Partial', 'Paid']}
            currentOption={requestData.status}
            onChange={(o) => changeRequestData('status', o)}
          />
        </div>

        <div className={classes.reps}>
          <Select
            label="All reps"
            nextOptionListRequest={nextOptionListRequest}
            options={selectRepList}
            totalPage={pageCount}
            currentPage={currentPage}
            limit={limit}
            currentOption={requestData.name}
            onChange={changeRep}
            optionPathname="name"
          />
        </div>

        <div className={classes.buttonContainer}>
          <Button title="Update" onClick={onUpdateClick} className={classes.updateButton} />

          <Button title="Clear" className={classes.updateButton} clearButton onClick={clearFilters} />
        </div>

      </div>

      <div className={classes.table_container}>
        {loading
          ? <Loader />

          : (
            <InvoiceTable
              onClick={onInvoiceClick}
              data={invoices}
              header={tableHeader}
              menuOptions={menuOptions}
              onMenuSelect={onMenuSelect}
            />
          )}
      </div>

      <RepBonusModal
        modalInfo={payModalInfo}
        onClose={closeModal}
        onPay={pay}
        changeData={changeModalData}
        payAmountError={payModalAmountError}
        savePaymentLoading={savePaymentLoading}
        setDeleteInfo={setDeleteModalInfo}
      />

      <AttentionModal
        onClose={() => setDeleteModalInfo({})}
        isOpen={!_.isEmpty(deleteModalInfo)}
        onSave={cancelPayment}
        title="Cancel Invoice?"
        text="Are you sure you want to cancel this invoice?"
        loading={savePaymentLoading}
      />
    </div>
  );
}
export default Invoices;
